/**
 * @fileoverview Vendor-specific business logic and class implementations for order management.
 * Contains base classes and vendor-specific implementations for different ticket providers
 * including Vivid Seats, TicketNetwork, GoTickets, and Resale markets.
 *
 * This module handles vendor-specific configurations, status management, tag systems,
 * Socket.IO communication, and UI rendering logic for each supported vendor.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

/* eslint-disable eqeqeq */

import { STATUS_ENUM, STATUS_NAME } from '../../../utils/enum';
import React from 'react';
import { EventEmitter } from 'events';

/**
 * List of status values that are excluded from being editable in the UI.
 * These statuses typically represent system-controlled or read-only states.
 *
 * @type {Array<number>}
 * @constant
 */
const excludedEditableStatuses = [STATUS_ENUM.ProblemOrders, STATUS_ENUM.Missing, STATUS_ENUM.AutoProcessing, STATUS_ENUM.Decreasing, STATUS_ENUM.Transfering, STATUS_ENUM.Unconfirmed];

/**
 * Event constants for the internal event bus system.
 * Used for coordinating real-time updates and inter-component communication.
 *
 * @type {Object<string, string>}
 * @constant
 */
const bus_events = {
    opening_orders: 'opening_orders',
    grid_completed: 'grid_completed',
    grid_before_send: 'grid_before_send',
    grid_is_refreshing: 'grid_is_refreshing',
    grid_selection_changed: 'grid_selection_changed',
    grid_order_shown: 'grid_order_shown',
    refresh_highlight_box: 'refresh_highlight_box',
    order_status_changed: 'order_status_changed',
    order_status_changing: 'order_status_changing',
    refresh_grid: 'refresh_grid',
    order_saved: 'order_saved',
    order_note_saved: 'order_note_saved',
    order_fetch_newdata: 'order_fetch_newdata',
    receipt_available: 'receipt_available',
    broadcast: 'broadcast', //used by VividIO.
};

/**
 * Common tags shared across all vendor implementations.
 * These tags represent business states and conditions that apply universally.
 *
 * @type {Array<string>}
 * @constant
 */
const sharedTags = [
    'Problem', 'Offered Alts', 'Will Call', 'Mistake', 'Denied Entry', 'Canceled Event', 'AP Skip', 'Skip Sync', 'Waiting to Process',
    'Fast', 'Gecko', 'Non Transferable', 'POSTPONED', 'Needs to be refunded', 'Check CC refund', 'Urgent', 'Divvy', 'BLUMENTHAL - SPECIAL ACCT REQ\'D', 'Special Accts', 'Shipping Mistake', 'Processing Mistake', 'Mapping Error',
    'venue refunded', 'Order Support', 'View Issue', 'Nonrefundable', 'Venue Converted to WC', 'Wait To Ship', 'Pending Venue Response', 'Unable To Process', 'Delivery', 'Account Surrender',
    'Missed IHD', 'Mutual', 'Prio', 'Missing', 'Awaiting Exchange Action', 'AP Purchase Failed', 'GL Gmail', 'CTO', 'Account Access Only', 'Email FWD', 'Fix', 'Spec', 'Retransferred', 'Refresh Triggered',
    'NO DUMMY ACCTS', 'Dummy accts ONLY'];

/**
 * Legacy/historical tags that are no longer actively used but may exist in historical data.
 * These tags are maintained for backwards compatibility and data integrity.
 *
 * @type {Array<string>}
 * @constant
 */
const historicalTags = ['Gremlin', 'Gremlin Resolved', 'Owl', 'Hoot', 'Transfer Issue', 'Busted', 'received hard ticket', 'Will-Call', 'Awaiting VS Action', 'Awaiting TN Action'];

/**
 * Base application class providing common functionality for all vendor implementations.
 * Contains shared methods for status management, tag handling, UI rendering, and Socket.IO communication.
 *
 * @class
 */
class App {
    protected bus: EventEmitter;
    /**
     * Creates a new App instance with default configuration.
     *
     * @constructor
     */
    constructor () { };
    //#region Socket-IO
    getSocketIONameSpace = () => 'vivid';
    getSocketIOEndPoint = (url) => {
        let endPoint = 'wss://' + (url || 'io.songtek.net') + '/' + this.getSocketIONameSpace();
        return endPoint;
    };
    createBus = () => {
        let bus = new EventEmitter() as any;

        // Add broadcast method
        bus.broadcast = function (msg, data) {
            return bus.emit(bus_events.broadcast, msg, data);
        };

        // Add emitAsync method
        bus.emitAsync = async function (event, ...args) {
            const listeners = this.listeners(event);
            const promises = listeners.map((fn: any) => fn(...args));
            return Promise.all(promises); // or use allSettled for safer parallel execution
        };

        console.log('Create bus....');
        this.bus = bus;
    };
    //#endregion

    getEditableStatuses (allowed, excluded: any[] = []) {
        excluded = excluded.concat(excludedEditableStatuses as any);
        return (allowed || Object.keys(STATUS_ENUM).map((k: any) => STATUS_ENUM[k])).filter((x: any) => excluded.indexOf(x) == -1)
            .map((status: any) => ({ name: STATUS_NAME[status], value: status, id: status }));
    };
    // getStatuses(allowed, excluded = []) {
    //     return (allowed || Object.keys(STATUS_ENUM).map(k => STATUS_ENUM[k])).filter(x => excluded.indexOf(x) == -1)
    //         .map(status => ({ name: STATUS_NAME[status], value: status }));
    // };
    getStatuses (allowed, excluded: any[] = []) {
        let ids = (allowed || Object.values(STATUS_ENUM));
        let mapped = Object.entries(STATUS_ENUM)
            .map((x: any) => ({ id: x[1], value: x[0], name: STATUS_NAME[x[1]] }))
            .filter((x: any) => ids.some(y => y == x.id))
            .filter((x: any) => excluded.indexOf(x.id) == -1);

        if (allowed) { // An array with a previously sorted order
            mapped = allowed.map((x: any) => mapped.find((y: any) => y.id == x));
        }
        //The OData of enum type sent to the backend must be in string format, so the id needs to be converted to a string.
        return mapped.filter((x: any) => x).map((m: any) => ({ ...m, id: m.value }));
    };
    checkIfUseStrOrderNumber = () => false;
    hideShippingFilter = () => true;
    buildEventBlock = (record) => {
        return (<>
            <span>{record.event}</span><br />
            <span style={{ fontSize: '12px' }}>{record.venue}</span><br />
            {record.event_market &&
                (<a href={record.event_url} target='_blank' style={{ textDecoration: 'none' }}>
                    <span className='custom-Tag' style={{ backgroundColor: '#337ab7', fontSize: '13px' }}>{record.event_market}</span>
                </a>)
            }
        </>);
    };
    checkIfNeedSyncNote = () => false;
    prettifyAuthorName = (author) => {
        if (author.length == 0) return 'TN';
        let tmp = author;
        if (tmp.indexOf('@') > -1) tmp = author.split('@')[0];
        //now, author can be "EXT" (purchase extension).
        let value = tmp.charAt(0).toUpperCase() + tmp.slice(1);
        return value;
    };
    //force to override
}

//#region Vivid
// ---------------------------------
// Vivid
// ---------------------------------
class Vivid extends App {
    constructor () {
        super();
    };

    getSocketIONameSpace = () => 'vivid';
    getEditableStatuses () {
        return super.getEditableStatuses(null, [STATUS_ENUM.TNProcessing] as any);
    };
    getStatuses () {
        return super.getStatuses(null, [STATUS_ENUM.TNProcessing] as any);//.concat([statusHardTicket, statusFedex, statusAirBill, statusAPFailed]);
    };
    getTags () {
        return ['Tickets On Sale', 'Rejected', 'Real'].concat(sharedTags);
    }
    shouldRemoveProblemTag = () => true;

    getTitle = () => 'Vivid Orders';
}
//#endregion

//#region TicketNetwork
// ---------------------------------
// TicketNetwork
// ---------------------------------
class TicketNetwork extends App {
    private allowedStatuses: number[];

    constructor () {
        super();
        this.allowedStatuses = [
            STATUS_ENUM.Unconfirmed, STATUS_ENUM.Unprocessed, STATUS_ENUM.TNProcessing, STATUS_ENUM.Shipping, STATUS_ENUM.PendingShipment, STATUS_ENUM.DelayedUnshippedOrders,
            STATUS_ENUM.Completed, STATUS_ENUM.ProblemOrders, STATUS_ENUM.Rejected, STATUS_ENUM.Canceled, STATUS_ENUM.Transfering, STATUS_ENUM.Undelivered
        ];
    };
    getSocketIONameSpace = () => 'ticketnetwork';
    getEditableStatuses () {
        return super.getEditableStatuses(this.allowedStatuses);
    };
    getStatuses () {
        return super.getStatuses(this.allowedStatuses);//.concat([statusHardTicket, statusFedex, statusAPFailed]);
    };
    getTags () {
        //https://app.getflow.com/organizations/344018/teams/292068/tasks/22348033
        return ['Preconfirm Rejected', 'Auto-Rejected'].concat(sharedTags);
    };
    getTitle = () => 'TicketNetwork Orders';
    hideShippingFilter = () => false;
    buildEventBlock = (record) => {
        return (<>
            <span>{record.event}</span><br />
            <span style={{ fontSize: '12px' }}>{record.venue}</span><br />
            <span style={{ justifyContent: 'space-between', display: 'flex' }}>
                <a href={record.event_url} target='_blank' style={{ textDecoration: 'none' }}>
                    <span className='custom-Tag' style={{ backgroundColor: '#337ab7', fontSize: '13px' }}>{record.event_market}</span>
                </a>
                <strong>{`$${record.wholesale > 0 ? record.wholesale : (record.unit_list_price || 0)}/ $${record.order_total}`}</strong>
            </span>
        </>);
    };
    checkIfNeedSyncNote = () => true;
}
//#endregion

//#region GoTickets
// ---------------------------------
// GoTickets
// ---------------------------------

class GoTickets extends App {
    getSocketIONameSpace = () => 'gotickets';
    getTags () {
        return ['Tickets On Sale', 'Rejected'].concat(sharedTags);
    };
    getEditableStatuses () {
        return super.getEditableStatuses(null, [STATUS_ENUM.TNProcessing] as any);
    };
    getStatuses () {
        const NotUploadedYet = { id: 'NotUploadedYet', name: STATUS_NAME[16] };
        return super.getStatuses(null, [STATUS_ENUM.TNProcessing] as any).concat([NotUploadedYet]);
    };

    getTitle = () => 'GoTickets Orders';
}
//#endregion

//#region Resale
// ---------------------------------
// Resale
// ---------------------------------
class Resale extends Vivid {
    constructor () {
        super();
    };

    getSocketIONameSpace = () => 'resale';
    getTags () {
        return ['Tickets On Sale', 'Rejected', 'Real', 'Upcoming', 'Unsold', 'Unbroadcasted', 'Dq', 'Nps', 'Sec', 'Nm'].concat(sharedTags);
    };
    checkIfUseStrOrderNumber = () => true;
    getTitle = () => 'Resale Orders';
}
//#endregion

function create (controller) {
    let vendorMap = {
        'Vivid': function () { return new Vivid(); },
        'TicketNetwork': function () { return new TicketNetwork(); },
        'GoTickets': function () { return new GoTickets(); },
        // "SeatGeek": function () { return new SeatGeek(); },
        // "TickPick": function () { return new TickPick(); },
        // "Gany": function () { return new Gany(); },
        'Resale': function () { return new Resale(); }
    };

    let app = vendorMap[controller]();
    app.controller = controller; // backend controller
    app.createBus();
    return app;
}

export { create, historicalTags, bus_events, sharedTags };